/** Maximum byte length for any single string field in log context */
const MAX_FIELD_LENGTH = 64 * 1024; // 64 KB
/** Maximum nesting depth for log context objects */
const MAX_DEPTH = 10;
/** Truncate a string value if it exceeds MAX_FIELD_LENGTH */
function truncateField(value) {
    if (value.length > MAX_FIELD_LENGTH) {
        return value.slice(0, MAX_FIELD_LENGTH) + '... [TRUNCATED]';
    }
    return value;
}
/**
 * Flatten an Error (or axios-like error) into a plain object whose own
 * properties are enumerable. Native Error fields (name/message/stack) are
 * non-enumerable, so a downstream `Object.keys` walk would otherwise drop them.
 *
 * Axios-shaped fields (config/request/response) are picked explicitly and
 * pruned so noisy non-serializable bits (functions, the full request stream,
 * etc.) never reach the log. Any remaining own-enumerable properties are
 * passed through verbatim — this is what surfaces `killed`/`signal`/`cmd`/
 * `stdout`/`stderr` for `child_process.exec` errors instead of burying them
 * inside the concatenated `message`.
 */
function flattenError(value) {
    const KNOWN_KEYS = new Set(['name', 'message', 'stack', 'code', 'status', 'statusText', 'config', 'request', 'response']);
    const result = {
        name: value.name,
        message: value.message,
        stack: value.stack,
        code: value.code,
        status: value.status,
        statusText: value.statusText,
        config: value.config
            ? {
                method: value.config.method,
                url: value.config.url,
                baseURL: value.config.baseURL,
                headers: value.config.headers,
                timeout: value.config.timeout,
                data: value.config.data,
            }
            : undefined,
        request: value.request
            ? {
                method: value.request.method,
                url: value.request.url,
                headers: value.request.headers,
            }
            : undefined,
        response: value.response
            ? {
                status: value.response.status,
                statusText: value.response.statusText,
                headers: value.response.headers,
                data: value.response.data,
            }
            : undefined,
    };
    for (const key of Object.keys(value)) {
        if (!KNOWN_KEYS.has(key)) {
            result[key] = value[key];
        }
    }
    return result;
}
/** Recursively cap depth and truncate large string fields */
function capDepthAndSize(value, currentDepth) {
    if (value === null || value === undefined)
        return value;
    if (typeof value === 'string')
        return truncateField(value);
    if (typeof value === 'bigint')
        return value.toString() + 'n';
    if (typeof value !== 'object')
        return value;
    if (currentDepth >= MAX_DEPTH)
        return '[Max depth exceeded]';
    if (value instanceof Error) {
        return capDepthAndSize(flattenError(value), currentDepth);
    }
    if (Array.isArray(value)) {
        return value.map(item => capDepthAndSize(item, currentDepth + 1));
    }
    const result = {};
    for (const key of Object.keys(value)) {
        result[key] = capDepthAndSize(value[key], currentDepth + 1);
    }
    return result;
}
export const logErrorReplacer = (_key, value) => {
    if (value instanceof Error) {
        return capDepthAndSize(flattenError(value), 0);
    }
    return value;
};
export function createLogCircularReplacer() {
    const seen = new WeakSet();
    return (key, value) => {
        if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) {
                return '[Circular Reference]';
            }
            seen.add(value);
        }
        return logErrorReplacer(key, value);
    };
}
/**
 * JSON-stringify a log context, handling Error objects and circular references.
 * Falls back to a JSON-quoted placeholder string if serialization fails entirely.
 */
export function safeStringifyLogContext(context, indent) {
    try {
        const capped = capDepthAndSize(context, 0);
        return JSON.stringify(capped, logErrorReplacer, indent);
    }
    catch {
        try {
            const capped = capDepthAndSize(context, 0);
            return JSON.stringify(capped, createLogCircularReplacer(), indent);
        }
        catch {
            return JSON.stringify('[Non-serializable object]');
        }
    }
}
/**
 * Produce a plain, structured-clone-safe copy of a value by round-tripping
 * through JSON using the same replacers used for file logging. Required before
 * sending log payloads over Electron IPC (webContents.send), which otherwise
 * crashes on Error instances, class instances, and circular references.
 */
export function sanitizeForStructuredClone(value) {
    return JSON.parse(safeStringifyLogContext(value));
}
//# sourceMappingURL=serialization.js.map